import React, { useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Context } from '../../../store';
import { getAlbumArt } from '../../../services/albumArtService';
import artistFrame from '../../../images/artist_frame.svg';
import zaglushkaLogo from '../../../images/zaglushka_logo.png';

import HistoryPopup from '../components/HistoryPopup';
import { ReactComponent as PlaylistBtnIcon } from '../../../images/playlist-btn.svg';
import { reportTrackFeedback } from '../../../services/reportingService';

const PlayerScreen = ({ isVisible }) => {
    const [state, dispatch] = useContext(Context);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const containerRef = useRef(null);
    const isInitialMount = useRef(true);
    const isProgrammaticScroll = useRef(false);
    const touchStartX = useRef(null);

    const streams = useMemo(() => ['myata', 'gold', 'myata_hits'], []);
    const streamClasses = {
        myata: 'stream-myata',
        gold: 'stream-gold',
        myata_hits: 'stream-xtra'
    };

    // Accent colors - RESTORED UNIQUE STATION IDENTITY
    const streamAccentColors = {
        myata: '#00E5FF',     // Голубой/Cyan
        gold: '#FFFF00',      // Жёлтый
        myata_hits: '#FFCCFF' // Розовый (для xtra)
    };

    const accentColor = streamAccentColors[state.stream] || '#00E5FF';

    // Use global albumArts from context for persistence across navigation
    const albumArts = useMemo(() => state.albumArts || {}, [state.albumArts]);

    const currentStreamIndex = streams.indexOf(state.stream);

    // Update Media Session API for Lock Screen / Control Center
    const updateMediaSession = useCallback((artist, track, artworkUrl) => {
        if ('mediaSession' in navigator) {
            try {
                // Ensure defaultArt is a full URL for the lock screen
                const defaultArt = window.location.origin + zaglushkaLogo;
                const finalArt = artworkUrl || defaultArt;

                const artwork = [
                    { src: finalArt, sizes: '96x96', type: artworkUrl ? 'image/jpeg' : 'image/png' },
                    { src: finalArt, sizes: '128x128', type: artworkUrl ? 'image/jpeg' : 'image/png' },
                    { src: finalArt, sizes: '256x256', type: artworkUrl ? 'image/jpeg' : 'image/png' },
                    { src: finalArt, sizes: '512x512', type: artworkUrl ? 'image/jpeg' : 'image/png' },
                ];

                navigator.mediaSession.metadata = new MediaMetadata({
                    title: track && track !== 'Загрузка...' ? track : 'Radio Myata',
                    artist: artist && artist !== 'Radio Myata' ? artist : 'Radio Myata',
                    album: 'Radio Myata',
                    artwork: artwork
                });

                // Set action handlers
                navigator.mediaSession.setActionHandler('play', () => {
                    const audio = state.audio;
                    if (!state.nowPlaying) {
                        audio.src = 'https://radio.dline-media.com/' + state.stream;
                        audio.load();
                        audio.play();
                        dispatch({ nowPlaying: true });
                    }
                });
                navigator.mediaSession.setActionHandler('pause', () => {
                    const audio = state.audio;
                    audio.pause();
                    audio.src = '';
                    dispatch({ nowPlaying: false });
                });
                navigator.mediaSession.setActionHandler('stop', () => {
                    const audio = state.audio;
                    audio.pause();
                    audio.src = '';
                    dispatch({ nowPlaying: false });
                });

                console.log('[MediaSession] Updated:', artist, '-', track);
            } catch (error) {
                console.warn('[MediaSession] Error:', error);
            }
        }
    }, [state.audio, state.stream, state.nowPlaying, dispatch]);

    // Fetch album art function
    const updateAlbumArtForStream = useCallback(async (st) => {
        const streamInfo = state.trackInfo ? state.trackInfo[st] : null;
        const artistName = streamInfo?.artist || 'Radio Myata';
        const trackName = streamInfo?.track || 'RADIO MYATA';
        const currentCachedArt = state.albumArts?.[st];

        if (artistName && trackName && artistName !== 'Radio Myata' && artistName !== 'YOUR MUSIC! YOUR STATION!') {
            const art = await getAlbumArt(artistName, trackName);

            // Dispatch to global state with metadata for cache validation
            dispatch({
                albumArts: {
                    [st]: { url: art, artist: artistName, track: trackName }
                }
            });

            // Update Media Session for current stream
            if (st === state.stream) {
                updateMediaSession(artistName, trackName, art);
            }
        } else {
            // API returned placeholder - DON'T clear existing artwork if we had valid data
            // This prevents flickering when API temporarily returns "YOUR MUSIC! YOUR STATION!"
            const hadValidArt = currentCachedArt?.url && 
                currentCachedArt?.artist && 
                currentCachedArt?.artist !== 'Radio Myata' && 
                currentCachedArt?.artist !== 'YOUR MUSIC! YOUR STATION!';
            
            if (hadValidArt) {
                // Keep existing artwork, don't update state - API returned temporary placeholder
                console.log(`[PlayerScreen] Keeping existing art for ${st}, API returned placeholder`);
            } else {
                // No valid art existed - this is initial state or truly no track
                dispatch({
                    albumArts: {
                        [st]: { url: null, artist: artistName, track: trackName }
                    }
                });
                if (st === state.stream) {
                    updateMediaSession('Radio Myata', 'Radio Myata', null);
                }
            }
        }
    }, [state.trackInfo, state.stream, state.albumArts, updateMediaSession, dispatch]);

    // Update album arts when trackInfo changes, using global state for persistence
    useEffect(() => {
        streams.forEach(st => {
            const currentInfo = state.trackInfo?.[st];
            const cachedArt = albumArts[st];

            // Check if cached art matches current track
            const needsUpdate = !cachedArt ||
                cachedArt.artist !== currentInfo?.artist ||
                cachedArt.track !== currentInfo?.track;

            if (needsUpdate && currentInfo) {
                updateAlbumArtForStream(st);
            }
        });
    }, [state.trackInfo, updateAlbumArtForStream, streams, albumArts]);

    // Handle scroll to switch streams
    const handleScroll = (e) => {
        // If we're doing a programmatic scroll or history is open, don't trigger state updates
        if (isProgrammaticScroll.current || isHistoryOpen) {
            if (isHistoryOpen) {
                // Force scroll back if user tries to scroll while history is open
                const width = e.target.offsetWidth;
                e.target.scrollLeft = currentStreamIndex * width;
            }
            return;
        }

        const scrollLeft = e.target.scrollLeft;
        const width = e.target.offsetWidth;
        if (width === 0) return;

        const index = Math.round(scrollLeft / width);

        if (streams[index] && streams[index] !== state.stream) {
            // Ignore scroll events during initial mount
            if (isInitialMount.current) return;
            dispatch({ stream: streams[index] });
        }
    };

    // Keep scroll position in sync with state.stream
    useEffect(() => {
        if (containerRef.current && isVisible) {
            requestAnimationFrame(() => {
                const width = containerRef.current.offsetWidth;
                if (width === 0) return;

                const targetScroll = currentStreamIndex * width;

                if (Math.abs(containerRef.current.scrollLeft - targetScroll) > 5) {
                    isProgrammaticScroll.current = true;
                    const behavior = isInitialMount.current ? 'auto' : 'smooth';

                    containerRef.current.scrollTo({
                        left: targetScroll,
                        behavior: behavior
                    });

                    // Unlock after animation finishes (rough estimation)
                    const timeout = isInitialMount.current ? 0 : 400;
                    setTimeout(() => {
                        isProgrammaticScroll.current = false;
                        isInitialMount.current = false;
                    }, timeout);
                } else {
                    isInitialMount.current = false;
                }
            });
        }
    }, [state.stream, currentStreamIndex, isVisible]);

    // AUTO-SWITCH AUDIO when stream changes (during swipe)
    useEffect(() => {
        const audio = state.audio;
        if (state.nowPlaying && audio.src !== 'https://radio.dline-media.com/' + state.stream) {
            console.log(`[Player] Auto-switching audio to: ${state.stream}`);
            dispatch({ isLoading: true });
            audio.pause();
            audio.src = 'https://radio.dline-media.com/' + state.stream;
            audio.load();
            audio.play()
                .then(() => dispatch({ isLoading: false }))
                .catch(err => {
                    console.error('Auto-switch error:', err);
                    dispatch({ isLoading: false });
                });
        }
    }, [state.stream, state.audio, state.nowPlaying, dispatch]); // Only listen to stream changes

    useEffect(() => {
        const currentInfo = state.trackInfo ? state.trackInfo[state.stream] : null;
        const artistName = currentInfo?.artist || 'Radio Myata';
        const trackName = currentInfo?.track || 'RADIO MYATA';
        const currentArt = albumArts[state.stream]?.url;

        updateMediaSession(artistName, trackName, currentArt);
    }, [state.stream, state.trackInfo, albumArts, updateMediaSession]);

    // Playback logic
    const togglePlay = () => {
        const audio = state.audio;
        if (state.nowPlaying) {
            // STOP stream completely (not just pause) for live radio
            audio.pause();
            audio.src = '';       // Clear source to stop loading stream
            audio.load();         // Reset audio element to prevent buffered content
            dispatch({ nowPlaying: false });
        } else {
            dispatch({ isLoading: true });
            audio.src = 'https://radio.dline-media.com/' + state.stream;
            audio.play()
                .then(() => {
                    dispatch({ nowPlaying: true });
                    dispatch({ isLoading: false });
                })
                .catch((err) => {
                    console.error('Playback error:', err);
                    dispatch({ isLoading: false });
                });
        }
    };

    const switchStream = (newStream) => {
        if (newStream === state.stream) return;
        isProgrammaticScroll.current = true;
        dispatch({ stream: newStream });
    };

    const toggleFavorite = () => {
        const currentInfo = state.trackInfo ? state.trackInfo[state.stream] : null;
        if (!currentInfo || !currentInfo.artist || currentInfo.artist === 'Radio Myata') return;

        const trackId = `${currentInfo.artist} - ${currentInfo.track}`;
        const isFav = state.favorites?.some(f => `${f.artist} - ${f.track}` === trackId);

        if (isFav) {
            // Remove
            const newFavs = state.favorites.filter(f => `${f.artist} - ${f.track}` !== trackId);
            dispatch({ favorites: newFavs });
            reportTrackFeedback({
                artist: currentInfo.artist,
                track: currentInfo.track,
                stream: state.stream,
                type: 'dislike'
            });
        } else {
            // Add
            const newFav = {
                artist: currentInfo.artist,
                track: currentInfo.track,
                addedAt: new Date().toLocaleString(),
                stream: state.stream
            };
            dispatch({ favorites: [...(state.favorites || []), newFav] });
            reportTrackFeedback({
                artist: currentInfo.artist,
                track: currentInfo.track,
                stream: state.stream,
                type: 'like'
            });
        }
    };

    const isCurrentTrackFavorite = useMemo(() => {
        const currentInfo = state.trackInfo ? state.trackInfo[state.stream] : null;
        if (!currentInfo) return false;
        const trackId = `${currentInfo.artist} - ${currentInfo.track}`;
        return state.favorites?.some(f => `${f.artist} - ${f.track}` === trackId);
    }, [state.favorites, state.trackInfo, state.stream]);

    // Touch handlers for full-screen swipe
    const handleTouchStart = (e) => {
        if (isHistoryOpen) return;
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current === null || isHistoryOpen) return;

        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX.current - touchEndX;
        const threshold = 50; // Minimum swipe distance

        if (Math.abs(diff) > threshold) {
            const currentIndex = streams.indexOf(state.stream);
            if (diff > 0 && currentIndex < streams.length - 1) {
                // Swipe left - next stream
                switchStream(streams[currentIndex + 1]);
            } else if (diff < 0 && currentIndex > 0) {
                // Swipe right - previous stream
                switchStream(streams[currentIndex - 1]);
            }
        }

        touchStartX.current = null;
    };

    return (
        <div
            className={`mobile-screen player-screen-wrapper ${streamClasses[state.stream]}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {/* Dots UI */}
            <div className="stream-dots">
                {streams.map((st, index) => (
                    <div
                        key={st}
                        className={`dot ${st === state.stream ? 'active' : ''}`}
                        onClick={() => switchStream(st)}
                    />
                ))}
            </div>

            {/* Horizontal Slider Area */}
            <div
                ref={containerRef}
                className="player-slider-container"
                onScroll={handleScroll}
            >
                {streams.map((st) => {
                    const stTrackInfo = state.trackInfo ? state.trackInfo[st] : null;
                    const stArtist = stTrackInfo?.artist || 'Radio Myata';
                    const stTrack = stTrackInfo?.track || 'Загрузка...';

                    return (
                        <div key={st} className="player-slide">
                            <div className="artist-photo-container">
                                <div className="artist-photo-frame-svg">
                                    <img src={artistFrame} alt="" className="artist-frame-bg" />
                                    <div className="artist-photo-content">
                                        {albumArts[st]?.url ? (
                                            <img
                                                src={albumArts[st].url}
                                                alt={stArtist}
                                                className="artist-photo"
                                                referrerPolicy="no-referrer"
                                                crossOrigin="anonymous"
                                                onError={(e) => {
                                                    // Try PHP proxy as fallback
                                                    if (!e.target.dataset.triedProxy) {
                                                        e.target.dataset.triedProxy = 'true';
                                                        // Use URL proxy mode (passes same URL through PHP)
                                                        e.target.src = `/albumart_image.php?url=${encodeURIComponent(albumArts[st].url)}`;
                                                    } else {
                                                        e.target.style.display = 'none';
                                                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <div
                                            className="artist-photo-placeholder"
                                            style={{ display: albumArts[st]?.url ? 'none' : 'flex' }}
                                        >
                                            <img src={zaglushkaLogo} alt="Radio Myata" className="artist-photo" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="track-info-mobile">
                                <h2 className="artist-name" style={{ color: streamAccentColors[st] }}>{stArtist}</h2>
                                <p className="track-name">{stTrack}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Fixed Play Button */}
            <div className="play-button-container">
                <button
                    className={`play-button ${state.nowPlaying ? 'playing' : ''}`}
                    onClick={togglePlay}
                    disabled={state.isLoading}
                    style={{ backgroundColor: accentColor }}
                >
                    {state.isLoading ? (
                        <div className="loading-spinner"></div>
                    ) : state.nowPlaying ? (
                        <svg viewBox="0 0 24 24">
                            <rect x="6" y="4" width="4" height="16" rx="1.5" fill="none" stroke="#000" strokeWidth="2" />
                            <rect x="14" y="4" width="4" height="16" rx="1.5" fill="none" stroke="#000" strokeWidth="2" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24">
                            <polygon points="7,4 19,12 7,20" fill="none" stroke="#000" strokeWidth="2" strokeLinejoin="round" />
                        </svg>
                    )}
                </button>
                <button
                    className={`favorite-toggle-btn ${isCurrentTrackFavorite ? 'active' : ''}`}
                    onClick={toggleFavorite}
                    style={{ 
                        color: accentColor,
                        opacity: isCurrentTrackFavorite ? 1 : 0.8
                    }}
                >
                    <svg viewBox="0 0 24 24" width="28" height="28">
                        <path 
                            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                            fill={isCurrentTrackFavorite ? 'currentColor' : 'none'} 
                            stroke="currentColor" 
                            strokeWidth="2"
                        />
                    </svg>
                </button>
                <button
                    className="history-toggle-btn"
                    onClick={() => setIsHistoryOpen(true)}
                >
                    <PlaylistBtnIcon style={{ fill: accentColor, width: '28px', height: '28px' }} />
                </button>
            </div>

            <HistoryPopup 
                isOpen={isHistoryOpen} 
                onClose={() => setIsHistoryOpen(false)} 
                streamId={state.stream}
                accentColor={accentColor}
                backgroundColor={
                    state.stream === 'myata' ? '#9362AA' :
                    state.stream === 'gold' ? '#08AFB5' :
                    '#1C4771' // myata_hits/xtra
                }
            />
        </div>
    );
};

export default PlayerScreen;
